import { Avatar as AvatarPrimitive } from "radix-ui";
import type { ComponentProps, ElementType, ReactNode } from "react";
import { cn } from "../lib/cn";

/** The white elevated card every screen is built from. Pass `divide-y p-0` to stack sections. */
export function Container({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "w-full rounded-lg bg-ui-bg-base px-6 py-4 shadow-elevation-card-rest",
        className,
      )}
      {...props}
    />
  );
}

const headingStyles = {
  h1: "h1-core",
  h2: "h2-core",
  h3: "h3-core",
} as const;

export function Heading({
  level = "h1",
  className,
  ...props
}: ComponentProps<"h1"> & { level?: keyof typeof headingStyles }) {
  const Tag: ElementType = level;
  return <Tag className={cn(headingStyles[level], "text-ui-fg-base", className)} {...props} />;
}

type TextSize = "xsmall" | "small" | "base" | "large" | "xlarge";

const textSize: Record<TextSize, string> = {
  xsmall: "xsmall",
  small: "small",
  base: "medium",
  large: "large",
  xlarge: "xlarge",
};

export function Text({
  as: Tag = "p",
  size = "small",
  weight = "regular",
  leading = "compact",
  className,
  ...props
}: ComponentProps<"p"> & {
  as?: "p" | "span" | "div" | "dt" | "dd";
  size?: TextSize;
  weight?: "regular" | "plus";
  leading?: "normal" | "compact";
}) {
  const scale = `txt-${leading === "compact" ? "compact-" : ""}${textSize[size]}${
    weight === "plus" ? "-plus" : ""
  }`;
  return <Tag className={cn(scale, className)} {...props} />;
}

export function Kbd({ className, ...props }: ComponentProps<"kbd">) {
  return (
    <kbd
      className={cn(
        "txt-compact-xsmall-plus inline-flex h-5 min-w-5 items-center justify-center rounded-md border border-ui-tag-neutral-border bg-ui-tag-neutral-bg px-1 font-sans text-ui-tag-neutral-text",
        className,
      )}
      {...props}
    />
  );
}

export function Skeleton({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      aria-hidden="true"
      className={cn("animate-pulse rounded-md bg-ui-bg-component-pressed/70", className)}
      {...props}
    />
  );
}

export function Divider({
  orientation = "horizontal",
  variant = "solid",
  className,
  ...props
}: ComponentProps<"div"> & {
  orientation?: "horizontal" | "vertical";
  variant?: "solid" | "dashed";
}) {
  return (
    <div
      aria-orientation={orientation}
      role="separator"
      className={cn(
        "border-ui-border-base",
        orientation === "horizontal" ? "w-full border-t" : "h-full border-l",
        variant === "dashed" && "border-dashed",
        className,
      )}
      {...props}
    />
  );
}

const avatarSize = {
  "2xsmall": "size-5 txt-compact-xsmall-plus rounded-[5px]",
  xsmall: "size-6 txt-compact-xsmall-plus rounded-md",
  small: "size-7 txt-compact-small-plus rounded-md",
  base: "size-8 txt-compact-small-plus rounded-md",
  large: "size-10 txt-compact-medium-plus rounded-lg",
  xlarge: "size-12 txt-compact-large-plus rounded-xl",
} as const;

export function Avatar({
  src,
  fallback,
  size = "base",
  shape = "rounded",
  className,
}: {
  src?: string | null;
  /** One or two characters shown when there is no image. */
  fallback: string;
  size?: keyof typeof avatarSize;
  shape?: "rounded" | "circle";
  className?: string;
}) {
  return (
    <AvatarPrimitive.Root
      className={cn(
        "inline-flex shrink-0 select-none items-center justify-center overflow-hidden bg-ui-bg-component text-ui-fg-subtle shadow-borders-base",
        avatarSize[size],
        shape === "circle" && "rounded-full",
        className,
      )}
    >
      {src ? <AvatarPrimitive.Image src={src} alt="" className="size-full object-cover" /> : null}
      <AvatarPrimitive.Fallback className="uppercase">
        {fallback.slice(0, 2)}
      </AvatarPrimitive.Fallback>
    </AvatarPrimitive.Root>
  );
}

const bannerTones = {
  purple: "border-ui-tag-purple-border bg-ui-tag-purple-bg text-ui-tag-purple-text",
  orange: "border-ui-tag-orange-border bg-ui-tag-orange-bg text-ui-tag-orange-text",
} as const;

/**
 * A bar across the top of the app for a state the person must not lose sight
 * of, such as being signed in as someone else. The action sits at the end
 * and drops under the words on a phone.
 */
export function Banner({
  tone = "purple",
  icon,
  action,
  className,
  children,
}: {
  tone?: keyof typeof bannerTones;
  icon?: ReactNode;
  action?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      role="status"
      className={cn(
        "pt-safe flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b px-4 py-2",
        bannerTones[tone],
        className,
      )}
    >
      {icon ? <span className="flex shrink-0 items-center">{icon}</span> : null}
      <p className="txt-compact-small-plus min-w-0 flex-1 basis-[200px] text-pretty">{children}</p>
      {action ? <div className="flex shrink-0 items-center gap-x-2">{action}</div> : null}
    </div>
  );
}

/** Centered message for a list or screen with nothing in it yet. */
export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
}: {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col items-center px-6 py-16 text-center", className)}>
      {icon ? (
        <div className="mb-4 flex size-11 items-center justify-center rounded-xl bg-ui-bg-base text-ui-fg-subtle shadow-elevation-card-rest">
          {icon}
        </div>
      ) : null}
      <p className="txt-compact-medium-plus text-ui-fg-base">{title}</p>
      {description ? (
        <p className="txt-small mt-1 max-w-sm text-balance text-ui-fg-subtle">{description}</p>
      ) : null}
      {action ? <div className="mt-5">{action}</div> : null}
    </div>
  );
}

export function ProgressBar({
  value,
  className,
  tone = "interactive",
}: {
  /** 0 to 100. */
  value: number;
  className?: string;
  tone?: "interactive" | "neutral";
}) {
  const clamped = Math.min(100, Math.max(0, value));
  return (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(clamped)}
      className={cn("h-1.5 w-full overflow-hidden rounded-full bg-ui-bg-switch-off", className)}
    >
      <div
        className={cn(
          "h-full rounded-full transition-[width] duration-500 ease-out-expo",
          tone === "interactive" ? "bg-ui-bg-interactive" : "bg-ui-fg-base",
        )}
        style={{ width: `${clamped}%` }}
      />
    </div>
  );
}
